import { trace } from '@opentelemetry/api';

import { API_BASE_URL } from 'config';
import { headerInstancia } from 'modules/shared/instancia';
import type {
  AuthMe,
  AuthSessao,
  AuthPermission,
  AuthRole,
  AuthUser,
  CreatePermissionRequest,
  CreateRoleRequest,
  CreateUserRequest,
  LoginResponse,
  UpdatePermissionRequest,
  UpdateRoleRequest,
  UpdateUserRequest
} from 'types/domain';

interface AuthFetchOptions extends RequestInit {
  responseType?: 'blob';
}

interface ProblemLike {
  error?: string;
  detail?: string;
  title?: string;
  errors?: Record<string, string[]>;
  /** Código de negócio que acompanha alguns 400 — a tela decide o que oferecer a partir dele. */
  codigo?: string;
}

/** Erro de HTTP do `authFetch` com o status e, quando o backend manda, o código de negócio. */
export type ApiError = Error & { status?: number; codigo?: string };

export const TOKEN_KEY = 'rocky_token';
export const USER_KEY = 'rocky_user';
/** E-mail de quem entrou com Microsoft: a sessão pode ser renovada pelo Entra (ver renovacaoSessao). */
export const SESSAO_ENTRA_KEY = 'rocky_sessao_entra';

/**
 * Recupera a sessão depois de um 401 (a renovacaoSessao registra na carga). Recebe o token que a
 * chamada usou e devolve um token novo para repetir; o próprio token usado, quando ele acabou de
 * ser emitido e mesmo assim foi recusado (aí o 401 não é de sessão); ou null quando a sessão acabou.
 */
type RecuperacaoDe401 = (tokenUsado: string | null) => Promise<string | null>;
let recuperacaoDe401: RecuperacaoDe401 | null = null;

export function registrarRecuperacaoDe401(recuperar: RecuperacaoDe401): void {
  recuperacaoDe401 = recuperar;
}

/** Emissão do cookie de arquivo em andamento: várias imagens falhando juntas pedem uma vez só. */
let cookieDeArquivoEmAndamento: Promise<boolean> | null = null;

function erroApi(status: number, mensagem: string): ApiError {
  const err = new Error(mensagem) as ApiError;
  err.status = status;
  return err;
}

/**
 * 401 com a sessão recém-renovada: o endpoint quebrou o contrato de status (401 é só da camada de
 * autenticação; acesso negado é 403). Não desloga ninguém e vai para a telemetria.
 */
function registrarQuebraDoContrato401(url: string): void {
  const rota = url.split('?')[0];
  console.warn('[Sessão] 401 com a sessão válida: o endpoint quebrou o contrato de status', rota);
  const span = trace.getTracer('rocky-web').startSpan('auth.contrato_401_violado');
  span.setAttribute('http.route', rota);
  span.end();
}

// Usuário de empresa-cliente (portal) — usado na tela de gestão do Helpdesk.
export interface UsuarioClienteItem {
  id: string;
  nome: string;
  email: string;
  isActive: boolean;
  empresaId: string | null;
  empresaNome: string | null;
  ehClienteAdmin: boolean;
  convitePendente: boolean;
  /** Quantas empresas o usuário enxerga — >1 é escopo multi-entidade (Premier + Customiza). */
  empresasCount: number;
}

export interface PagedUsuariosCliente {
  items: UsuarioClienteItem[];
  total: number;
  page: number;
  pageSize: number;
}

export interface ListarUsuariosClienteParams {
  page: number;
  pageSize: number;
  q?: string;
  status?: string; // 'ativos' | 'inativos'
  convitePendente?: boolean;
}

// ─── Escopo por entidade (modelo GLPI): N empresas por usuário ───
// Premier e Customiza têm a mesma gestão e a mesma pessoa precisa ver os dois parques.

export interface UserEmpresaVinculo {
  empresaId: string;
  nome: string;
  documento: string | null;
  principal: boolean;
}

export interface UserEmpresasResponse {
  empresas: UserEmpresaVinculo[];
  /** Conjunto vazio = Boneside/entidade raiz: o usuário enxerga TUDO. */
  irrestrito: boolean;
  /** Escopo veio do User.EmpresaId legado — ainda não há vínculo materializado. */
  somenteLegado: boolean;
}

async function readProblem(response: Response): Promise<ProblemLike> {
  try {
    const body = await response.json();
    // TypedResults.BadRequest("mensagem") serializa o corpo como string JSON pura,
    // não como ProblemDetails — normaliza para não perder a mensagem do backend.
    if (typeof body === 'string') return { error: body };
    return (body ?? {}) as ProblemLike;
  } catch {
    return {};
  }
}

/**
 * Extrai o filename do header Content-Disposition.
 * Suporta os formatos: filename="x.pdf" e RFC 5987: filename*=UTF-8''x.pdf
 */
function parseContentDispositionFilename(header: string | null): string | null {
  if (!header) return null;
  // Prioriza filename* (RFC 5987 — pode incluir caracteres não-ASCII)
  const star = /filename\*\s*=\s*(?:UTF-8|utf-8)''([^;]+)/i.exec(header);
  if (star && star[1]) {
    try {
      return decodeURIComponent(star[1].trim());
    } catch {
      return star[1].trim();
    }
  }
  const plain = /filename\s*=\s*"?([^";]+)"?/i.exec(header);
  return plain && plain[1] ? plain[1].trim() : null;
}

export const authService = {
  async ssoExchange(idToken: string): Promise<LoginResponse> {
    const response = await fetch(`${API_BASE_URL}/api/auth/sso/exchange`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ idToken })
    });
    if (!response.ok) {
      if (response.status === 401) throw new Error('Token Microsoft inválido.');
      const data = await readProblem(response);
      throw new Error(data.detail || data.error || 'Falha no SSO.');
    }
    return (await response.json()) as LoginResponse;
  },

  async login(email: string, password: string): Promise<LoginResponse> {
    const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('E-mail ou senha inválidos.');
      }
      if (response.status === 403) {
        const data = await readProblem(response);
        throw new Error(data.detail || 'Conta desativada.');
      }
      throw new Error('Erro ao conectar com o servidor.');
    }

    return (await response.json()) as LoginResponse;
  },

  /** O status vai no erro: só 401 e 404 encerram a sessão; rede e 5xx são passageiros (AuthContext). */
  async getMe(token: string): Promise<AuthMe> {
    const response = await fetch(`${API_BASE_URL}/api/auth/me`, {
      headers: { Authorization: `Bearer ${token}` }
    });

    if (!response.ok) {
      throw erroApi(response.status, `Falha ao carregar a sessão (HTTP ${response.status}).`);
    }

    return (await response.json()) as AuthMe;
  },

  getToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  },

  /**
   * Bridge do fluxo OAuth (conectores MCP): cria a sessão do Authorization Server a partir do
   * JWT já autenticado e redireciona o browser pra URL de authorize, que então emite o code.
   * Usado quando o login foi aberto com `?oauth_return=<authorizeUrl>`.
   */
  async completeOAuthSession(returnUrl: string): Promise<void> {
    const token = this.getToken();
    const resp = await fetch(`${API_BASE_URL}/oauth/session`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      credentials: 'include'
    });
    if (!resp.ok) throw new Error('Sessão expirada. Entre novamente.');
    window.location.replace(returnUrl);
  },

  setToken(token: string): void {
    localStorage.setItem(TOKEN_KEY, token);
  },

  // `AuthSessao` e não `AuthMe`: o que se guarda pode ter vindo do login (sem `ehClienteAdmin`,
  // `empresaId` nem `empresaEmissoraAtivaId`) ou do /me (com os três) — ver `types/domain`.
  setUser(user: AuthSessao): void {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  },

  getStoredUser(): AuthSessao | null {
    const user = localStorage.getItem(USER_KEY);
    return user ? (JSON.parse(user) as AuthSessao) : null;
  },

  logout(): void {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(SESSAO_ENTRA_KEY);
    this.apagarCookieDeArquivo();
  },

  /**
   * Cookie de arquivo (T-484): o que autentica o `<img src>` que aponta direto para a API (imagem
   * inline do chamado), já que a tag não manda o Bearer. A API o emite a partir do Bearer atual,
   * com a mesma validade, como cookie HttpOnly e SameSite=Strict do host da API; ele só vale nos GET
   * de conteúdo de arquivo. O AuthContext chama a cada token novo (login, SSO, renovação, outra aba);
   * a imagem que falha chama de novo antes de desistir. Devolve se deu certo — falha não derruba nada,
   * só deixa a imagem no placeholder.
   */
  emitirCookieDeArquivo(): Promise<boolean> {
    const token = this.getToken();
    if (!token) return Promise.resolve(false);
    if (cookieDeArquivoEmAndamento) return cookieDeArquivoEmAndamento;
    cookieDeArquivoEmAndamento = fetch(`${API_BASE_URL}/api/auth/sessao-arquivos`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      credentials: 'include'
    })
      .then((r) => r.ok)
      .catch(() => false)
      .finally(() => {
        cookieDeArquivoEmAndamento = null;
      });
    return cookieDeArquivoEmAndamento;
  },

  /** Apaga o cookie de arquivo (logout). Sem Bearer: o token pode já ter vencido. */
  apagarCookieDeArquivo(): void {
    void fetch(`${API_BASE_URL}/api/auth/sessao-arquivos`, { method: 'DELETE', credentials: 'include' }).catch(() => undefined);
  },

  isAuthenticated(): boolean {
    return !!this.getToken();
  },

  hasPermission(permission: string): boolean {
    const user = this.getStoredUser();
    if (!user || !user.permissions) return false;
    return user.permissions.includes(permission);
  },

  /**
   * Política de 401 do app, para toda chamada autenticada (fetch ou XHR): `enviar` recebe o token e
   * faz a chamada. No 401, a sessão é recuperada (renovação forçada ou o diálogo de sessão
   * encerrada) e a chamada é repetida uma vez. Repetir é seguro: 401 da camada de autenticação quer
   * dizer que a chamada não chegou a executar.
   *
   * Contrato de status: 401 é só sessão (token ausente, vencido ou inválido); acesso negado é 403.
   * Por isso um 401 com a sessão recém-renovada vira erro na tela, sem logout.
   */
  async comSessao<R extends { status: number }>(url: string, enviar: (token: string | null) => Promise<R>): Promise<R> {
    const tokenUsado = this.getToken();
    const resposta = await enviar(tokenUsado);
    if (resposta.status !== 401) return resposta;

    const tokenNovo = recuperacaoDe401 ? await recuperacaoDe401(tokenUsado) : null;
    if (tokenNovo === null) throw erroApi(401, 'Sessão encerrada. Entre de novo para continuar.');
    if (tokenNovo !== tokenUsado) {
      const repetida = await enviar(tokenNovo);
      if (repetida.status !== 401) return repetida;
    }
    registrarQuebraDoContrato401(url);
    throw erroApi(401, 'O servidor recusou esta ação mesmo com a sessão válida. O erro foi registrado.');
  },

  /** fetch autenticado com a política de 401 (comSessao). Devolve a resposta crua: quem precisa do corpo de erro lê dela. */
  chamarApi(url: string, options: RequestInit = {}): Promise<Response> {
    return this.comSessao(url, (token) =>
      fetch(`${API_BASE_URL}${url}`, {
        ...options,
        headers: {
          ...(options.headers as Record<string, string> | undefined),
          Authorization: `Bearer ${token}`,
          // Os eventos de tempo real devolvem este id: é como esta aba reconhece o próprio eco.
          ...headerInstancia()
        }
      })
    );
  },

  async authFetch<T = unknown>(url: string, options: AuthFetchOptions = {}): Promise<T | null> {
    const isFormData = options.body instanceof FormData;
    const response = await this.chamarApi(url, {
      ...options,
      headers: {
        ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
        ...(options.headers as Record<string, string> | undefined)
      }
    });

    if (!response.ok) {
      if (response.status === 403) {
        // 403: autenticado, mas sem permissão — ou sem ALÇADA, que não é a mesma coisa.
        //
        // O `Forbid()` do ASP.NET devolve body vazio, e daí a mensagem padronizada. Mas há
        // handlers que respondem 403 com ProblemDetails porque o motivo não é dedutível da
        // rota: "ninguém aprova a própria ausência" e "aprovar esta ausência é do gestor direto
        // do colaborador" são recusas diferentes na MESMA rota, e trocar as duas por "sem
        // permissão para esta ação" manda a pessoa pedir um acesso que ela já tem. Quando o
        // backend explica, a explicação dele é que vale; body vazio continua caindo no texto
        // padrão, como antes.
        //
        // O `status` vai junto (como nos 400/404/409) porque há telas em que 403 não é "sem
        // permissão para a ação" e sim "este registro não é do seu escopo" — a ficha 360 do CRM
        // é o caso: a permissão abre a tela, o escopo decide quais contas. Sem o status, a única
        // pista seria o texto da mensagem, e mensagem não é contrato.
        const problema = await readProblem(response);
        const err = new Error(
          problema.error || problema.detail || problema.title || 'Sem permissão para esta ação.'
        ) as ApiError;
        err.status = 403;
        throw err;
      }

      if (response.status === 400) {
        const errorData = await readProblem(response);
        const validation = errorData.errors && typeof errorData.errors === 'object'
          ? Object.values(errorData.errors).flat().join(' | ')
          : null;
        const err = new Error(
          errorData.error || errorData.detail || validation || errorData.title || 'Erro na requisição.'
        ) as ApiError;
        err.status = 400;
        // Alguns 400 carregam um código (ex.: CLIENTE_DIVERGENTE no vínculo de cobrança) que muda
        // o que a tela oferece em seguida — sem ele a mensagem seria a única pista, e mensagem
        // não é contrato.
        if (errorData.codigo) err.codigo = errorData.codigo;
        throw err;
      }

      if (response.status === 404) {
        // O backend costuma dizer O QUE não foi encontrado ("a cobrança pay_x não existe no
        // Asaas") — engolir isso num texto genérico manda o operador adivinhar.
        const errorData = await readProblem(response);
        const err = new Error(errorData.error || errorData.detail || errorData.title || 'Não encontrado.') as ApiError;
        err.status = 404;
        throw err;
      }

      if (response.status === 409) {
        // Conflito (ex.: concorrência otimista). Body é a mensagem (string JSON) ou ProblemDetails.
        const txt = await response.text().catch(() => '');
        let msg = 'O item foi alterado por outra pessoa. Recarregue e tente novamente.';
        try {
          const j = JSON.parse(txt);
          msg = (typeof j === 'string' ? j : j.detail || j.error || j.title) || msg;
        } catch {
          if (txt) msg = txt;
        }
        const err = new Error(msg) as Error & { status?: number };
        err.status = 409;
        throw err;
      }

      if (response.status === 502 || response.status === 503) {
        // Provedor externo fora (Asaas, prefeitura): o backend diz qual e por quê no `detail`.
        // Repassar é o que deixa a tela distinguir "tente de novo" de "configure a integração".
        const errorData = await readProblem(response);
        const err = new Error(errorData.detail || errorData.error || errorData.title || 'Serviço externo indisponível.') as ApiError;
        err.status = response.status;
        throw err;
      }

      // O status vai junto também aqui (500, 504…): a tela diz "o servidor não respondeu" sem depender do texto.
      throw erroApi(response.status, 'Erro ao processar requisição.');
    }

    if (response.status === 204) return null;
    if (options.responseType === 'blob') return (await response.blob()) as unknown as T;
    const contentLength = response.headers.get('content-length');
    if (contentLength === '0') return null;
    const text = await response.text();
    if (!text) return null;
    return JSON.parse(text) as T;
  },

  async authFetchBlob(url: string): Promise<Blob> {
    const { blob } = await this.authFetchBlobWithName(url);
    return blob;
  },

  async authFetchBlobWithName(url: string): Promise<{ blob: Blob; filename: string | null }> {
    const response = await this.chamarApi(url);

    if (!response.ok) {
      let detail = '';
      try {
        const text = await response.text();
        if (text) {
          try {
            const j = JSON.parse(text) as ProblemLike;
            detail = j.error || j.detail || text;
          } catch {
            detail = text;
          }
        }
      } catch { /* ignore */ }
      throw new Error(`Erro ao baixar arquivo (HTTP ${response.status})${detail ? `: ${detail}` : ''}`);
    }
    const filename = parseContentDispositionFilename(response.headers.get('Content-Disposition'));
    const blob = await response.blob();
    return { blob, filename };
  },

  // Users
  listUsers(): Promise<AuthUser[] | null> {
    return this.authFetch<AuthUser[]>('/api/auth/users');
  },
  // Diretório interno de pessoas para atribuição (responsáveis, membros, técnicos…).
  // Desacoplado do endpoint sensível de gestão (auth.users.view): qualquer colaborador
  // interno pode montar o roster; clientes não. Ver ListUsuariosDiretorioEndpoint.
  listDiretorio(): Promise<AuthUser[] | null> {
    return this.authFetch<AuthUser[]>('/api/auth/users/diretorio');
  },
  // Diretório de usuários-cliente de uma empresa — responsável de um ativo no CMDB.
  listDiretorioCliente(empresaId: string): Promise<AuthUser[] | null> {
    return this.authFetch<AuthUser[]>(`/api/auth/users/cliente/diretorio?empresaId=${encodeURIComponent(empresaId)}`);
  },
  getUserById(id: string): Promise<AuthUser | null> {
    return this.authFetch<AuthUser>(`/api/auth/users/${id}`);
  },
  createUser(user: CreateUserRequest): Promise<AuthUser | null> {
    return this.authFetch<AuthUser>('/api/auth/users', {
      method: 'POST',
      body: JSON.stringify(user)
    });
  },
  updateUser(id: string, user: UpdateUserRequest): Promise<AuthUser | null> {
    return this.authFetch<AuthUser>(`/api/auth/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(user)
    });
  },
  deleteUser(id: string): Promise<null> {
    return this.authFetch<null>(`/api/auth/users/${id}`, {
      method: 'DELETE'
    }).then(() => null);
  },
  async promoverClienteAdmin(id: string, ehClienteAdmin: boolean): Promise<void> {
    await this.authFetch<null>(`/api/auth/users/${id}/cliente-admin`, {
      method: 'PUT',
      body: JSON.stringify({ ehClienteAdmin })
    });
  },
  // (Re)envia o convite de definição de senha a um usuário pendente/local.
  async reenviarConvite(userId: string): Promise<void> {
    await this.authFetch<null>(`/api/auth/users/${userId}/convite`, { method: 'POST' });
  },
  // Usuários de empresas-cliente (portal) — escopado a auth.users.provisionar_cliente. Paginado + filtros.
  async listarUsuariosCliente(params: ListarUsuariosClienteParams): Promise<PagedUsuariosCliente> {
    const qs = new URLSearchParams();
    qs.set('page', String(params.page));
    qs.set('pageSize', String(params.pageSize));
    if (params.q) qs.set('q', params.q);
    if (params.status) qs.set('status', params.status);
    if (params.convitePendente) qs.set('convitePendente', 'true');
    const res = await this.authFetch<PagedUsuariosCliente>(`/api/auth/users/cliente?${qs.toString()}`);
    return res ?? { items: [], total: 0, page: params.page, pageSize: params.pageSize };
  },
  async setUsuarioClienteAtivo(id: string, ativo: boolean): Promise<void> {
    await this.authFetch<null>(`/api/auth/users/cliente/${id}/ativo`, {
      method: 'PUT',
      body: JSON.stringify({ ativo })
    });
  },

  // ─── Empresas (entidades) que o usuário enxerga ───────────────
  // O escopo vem das claims empresa_id, emitidas no login: mudar aqui só vale no PRÓXIMO login.
  async listarEmpresasDoUsuario(userId: string): Promise<UserEmpresasResponse> {
    const res = await this.authFetch<UserEmpresasResponse>(`/api/auth/users/${userId}/empresas`);
    return res ?? { empresas: [], irrestrito: true, somenteLegado: false };
  },
  /** Define o conjunto inteiro — o que não for enviado é removido. */
  async definirEmpresasDoUsuario(userId: string, empresas: { empresaId: string; principal: boolean }[]): Promise<UserEmpresasResponse> {
    const res = await this.authFetch<UserEmpresasResponse>(`/api/auth/users/${userId}/empresas`, {
      method: 'PUT',
      body: JSON.stringify({ empresas })
    });
    return res ?? { empresas: [], irrestrito: true, somenteLegado: false };
  },

  // ─── Senha (definição/recuperação — endpoints anônimos) ───────
  // "Esqueci minha senha": responde sempre 200 (anti-enumeração), então não tratamos erro.
  async esqueciSenha(email: string): Promise<void> {
    await fetch(`${API_BASE_URL}/api/auth/senha/esqueci`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email })
    });
  },
  async validarTokenSenha(token: string): Promise<TokenSenhaInfo> {
    const resp = await fetch(`${API_BASE_URL}/api/auth/senha/validar?token=${encodeURIComponent(token)}`);
    if (!resp.ok) return { valido: false, tipo: null, nome: null };
    return (await resp.json()) as TokenSenhaInfo;
  },
  async definirSenha(token: string, novaSenha: string): Promise<void> {
    const resp = await fetch(`${API_BASE_URL}/api/auth/senha/definir`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, novaSenha })
    });
    if (!resp.ok) {
      const data = await readProblem(resp);
      const validation = data.errors && typeof data.errors === 'object'
        ? Object.values(data.errors).flat().join(' | ')
        : null;
      throw new Error(data.error || data.detail || validation || data.title || 'Não foi possível definir a senha.');
    }
  },

  // Roles
  listRoles(): Promise<AuthRole[] | null> {
    return this.authFetch<AuthRole[]>('/api/auth/roles');
  },
  getRole(id: string): Promise<AuthRole | null> {
    return this.authFetch<AuthRole>(`/api/auth/roles/${id}`);
  },
  createRole(role: CreateRoleRequest): Promise<AuthRole | null> {
    return this.authFetch<AuthRole>('/api/auth/roles', {
      method: 'POST',
      body: JSON.stringify(role)
    });
  },
  updateRole(id: string, role: UpdateRoleRequest): Promise<AuthRole | null> {
    return this.authFetch<AuthRole>(`/api/auth/roles/${id}`, {
      method: 'PUT',
      body: JSON.stringify(role)
    });
  },
  deleteRole(id: string): Promise<null> {
    return this.authFetch<null>(`/api/auth/roles/${id}`, {
      method: 'DELETE'
    }).then(() => null);
  },

  // Permissions
  listPermissions(): Promise<AuthPermission[] | null> {
    return this.authFetch<AuthPermission[]>('/api/auth/permissions');
  },
  getPermission(id: string): Promise<AuthPermission | null> {
    return this.authFetch<AuthPermission>(`/api/auth/permissions/${id}`);
  },
  createPermission(permission: CreatePermissionRequest): Promise<AuthPermission | null> {
    return this.authFetch<AuthPermission>('/api/auth/permissions', {
      method: 'POST',
      body: JSON.stringify(permission)
    });
  },
  updatePermission(id: string, permission: UpdatePermissionRequest): Promise<AuthPermission | null> {
    return this.authFetch<AuthPermission>(`/api/auth/permissions/${id}`, {
      method: 'PUT',
      body: JSON.stringify(permission)
    });
  },
  deletePermission(id: string): Promise<null> {
    return this.authFetch<null>(`/api/auth/permissions/${id}`, {
      method: 'DELETE'
    }).then(() => null);
  },

  // ─── Perfil (autoatendimento do próprio usuário) ──────────
  getPerfil(): Promise<Perfil | null> {
    return this.authFetch<Perfil>('/api/auth/me/perfil');
  },
  updatePerfil(payload: { nome: string; telefone?: string | null; cargo?: string | null }): Promise<Perfil | null> {
    return this.authFetch<Perfil>('/api/auth/me/perfil', { method: 'PUT', body: JSON.stringify(payload) });
  },
  uploadAvatar(file: File): Promise<{ avatarUrl: string } | null> {
    const fd = new FormData();
    fd.append('file', file);
    return this.authFetch<{ avatarUrl: string }>('/api/auth/me/avatar', { method: 'POST', body: fd });
  }
};

export interface Perfil {
  id: string;
  nome: string;
  email: string;
  avatarUrl: string | null;
  telefone: string | null;
  cargo: string | null;
  authProvider: string;
}

export interface TokenSenhaInfo {
  valido: boolean;
  tipo: 'Convite' | 'Redefinicao' | null;
  nome: string | null;
}
